import { createContext, useContext } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import type { Components } from 'react-markdown';

const ListTypeCtx = createContext<'ul' | 'ol'>('ul');

/**
 * Markdown del People Landing — mismo motor que BrainMarkdown pero con
 * tipografía de landing (h1/h2 reales, cuerpo 15px). Los links internos
 * (/people/landing/...) navegan por SPA.
 */
const components: Components = {
  p({ children }) {
    return <p className="text-[15px] leading-relaxed mb-3 last:mb-0 text-foreground/90">{children}</p>;
  },

  h1({ children }) {
    return <h1 className="font-display font-normal text-3xl md:text-4xl tracking-tight mt-2 mb-4 first:mt-0">{children}</h1>;
  },

  h2({ children }) {
    return <h2 className="font-display font-normal text-2xl md:text-3xl tracking-tight mt-2 mb-3 first:mt-0">{children}</h2>;
  },

  h3({ children }) {
    return <h3 className="text-lg font-semibold mt-2 mb-2 first:mt-0">{children}</h3>;
  },

  h4({ children }) {
    return <h4 className="text-base font-semibold mt-2 mb-1.5 first:mt-0">{children}</h4>;
  },

  ul({ children }) {
    return (
      <ListTypeCtx.Provider value="ul">
        <ul className="space-y-1.5 my-3">{children}</ul>
      </ListTypeCtx.Provider>
    );
  },

  ol({ children }) {
    return (
      <ListTypeCtx.Provider value="ol">
        <ol className="space-y-1.5 my-3 list-decimal pl-5">{children}</ol>
      </ListTypeCtx.Provider>
    );
  },

  // Falso positivo de react-hooks/rules-of-hooks: react-markdown
  // renderiza cada entrada de `components` COMO componente, así que useContext
  // acá corre durante un render legítimo. La regla no lo sabe porque el
  // nombre va en minúscula (tiene que coincidir con la etiqueta HTML).
  li({ children }) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const listType = useContext(ListTypeCtx);
    if (listType === 'ol') {
      return <li className="text-[15px] leading-relaxed pl-1">{children}</li>;
    }
    return (
      <li className="flex gap-2.5 text-[15px] leading-relaxed items-start">
        <span className="text-primary shrink-0 mt-0.5 leading-none">•</span>
        <span className="min-w-0">{children}</span>
      </li>
    );
  },

  blockquote({ children }) {
    return (
      <blockquote className="border-l-4 border-primary/40 bg-muted/30 rounded-r-lg px-4 py-3 my-4 text-[15px] text-muted-foreground">
        {children}
      </blockquote>
    );
  },

  hr() {
    return <hr className="my-6 border-border/60" />;
  },

  table({ children }) {
    return (
      <div className="my-4 border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm border-collapse">{children}</table>
      </div>
    );
  },

  thead({ children }) {
    return <thead className="bg-muted/40">{children}</thead>;
  },

  tbody({ children }) {
    return <tbody className="divide-y divide-border/50">{children}</tbody>;
  },

  th({ children }) {
    return (
      <th className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {children}
      </th>
    );
  },

  td({ children }) {
    return <td className="px-3 py-2 text-sm text-foreground/85">{children}</td>;
  },

  strong({ children }) {
    return <strong className="font-semibold text-foreground">{children}</strong>;
  },

  em({ children }) {
    return <em className="italic">{children}</em>;
  },

  code({ children }) {
    return (
      <code className="text-primary bg-primary/10 px-1.5 py-0.5 rounded text-[13px] font-mono break-all">
        {children}
      </code>
    );
  },

  a({ href, children }) {
    if (href?.startsWith('/')) {
      return (
        <Link
          to={href}
          className="text-primary font-medium underline underline-offset-2 hover:text-primary/80 transition-colors"
        >
          {children}
        </Link>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="text-primary font-medium underline underline-offset-2 hover:text-primary/80 transition-colors"
      >
        {children}
      </a>
    );
  },
};

/**
 * `size="lg"`: cuerpo 17px para bloques de texto a ancho completo (no hay plugin
 * typography en el proyecto, así que el equivalente a `prose-lg` se hace acá).
 */
export function LandingMarkdown({ content, size = 'md' }: { content: string; size?: 'md' | 'lg' }) {
  const body = (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
      {content}
    </ReactMarkdown>
  );
  if (size !== 'lg') return body;
  return (
    <div className="text-pretty [&_p]:text-[17px] [&_p]:leading-[1.65] [&_li]:text-[17px] [&_li]:leading-[1.65] [&_blockquote]:text-[16px]">
      {body}
    </div>
  );
}
